import { Controller } from '@hotwired/stimulus'
import type { TooltipPosition } from '../api/models/types'

let tooltipEl: HTMLElement | null = null
let contentEl: HTMLElement | null = null
let textEl: HTMLElement | null = null
let descriptionEl: HTMLElement | null = null
let arrowElement: HTMLElement | null = null

export default class extends Controller {
  declare textValue: string
  declare descriptionValue: string
  declare positionValue: TooltipPosition

  static values = {
    text: String,
    description: String,
    position: String,
  }

  connect() {
    if (this.textValue) {
      if (!tooltipEl) {
        //* I append the floating ui scripts to the head if they are not already there
        if (!document.getElementById('floating_ui_script_core')) {
          this.loadfloatingUIScriptCore()
        }

        if (!document.getElementById('floating_ui_script_dom')) {
          this.loadfloatingUIScriptDOM()
        }

        //* I append the css to the tooltip element
        const tooltipCSS = `[savvy-global-role="tooltip"] {
          background: #000000;
          color: #FFFFFF;
          padding: 6px 10px;
          border-radius: 5px;
          font-family: Work-sans, sans-serif;
          
          text-wrap: nowrap;
          z-index: 999999999;
          position: absolute;
          top: 0;
          left: 0;
        }
        [savvy-global-role="tooltip"] .tooltip_text {
          font-size: 13px;
          font-weight: 400;
          line-height: 1.3;
        }
        [savvy-global-role="tooltip"] .tooltip_description {
          font-size: 11px;
          font-weight: 300;
          line-height: 1.1;
          text-wrap: wrap;
        }
        [savvy-global-role="tooltip_arrow"] {
          background: #000000;
          position: absolute;
          z-index: 999999999;
          width: 8px;
          height: 8px;
          transform: rotate(45deg);
        }`
        const tooltipStyle = document.createElement('style')
        tooltipStyle.innerHTML = tooltipCSS
        tooltipStyle.id = 'tooltip_style'

        document.head.appendChild(tooltipStyle)

        //* I create the tooltip element
        tooltipEl = document.createElement('div')
        tooltipEl.classList.add('tooltip')
        tooltipEl.setAttribute('savvy-global-role', 'tooltip')
        tooltipEl.style.display = 'none'

        //* I append a div with the content with a text div and a description div
        contentEl = document.createElement('div')
        contentEl.classList.add('tooltip_content')
        tooltipEl.appendChild(contentEl)
        textEl = document.createElement('div')
        textEl.classList.add('tooltip_text')
        contentEl.appendChild(textEl)
        descriptionEl = document.createElement('div')
        descriptionEl.classList.add('tooltip_description')
        contentEl.appendChild(descriptionEl)

        //* I create the arrow element
        arrowElement = document.createElement('div')
        arrowElement.setAttribute('savvy-global-role', 'tooltip_arrow')

        tooltipEl.appendChild(arrowElement)
        document.body.appendChild(tooltipEl)
      }

      //* Add a listener to the element to show the tooltip on hover + focus
      this.element.addEventListener('mouseenter', this.showTooltip)
      this.element.addEventListener('focus', this.showTooltip)
      //* Add a listener to the element to hide the tooltip on hover out + click + blur
      this.element.addEventListener('mouseleave', this.hideTooltip)
      this.element.addEventListener('click', this.hideTooltip)
      this.element.addEventListener('blur', this.hideTooltip)

      this.element.removeAttribute('title')
    }
  }

  showTooltip = () => {
    if (!tooltipEl || !arrowElement || !textEl || !descriptionEl) return
    //* First set the text & description
    textEl.innerHTML = this.textValue

    if (this.descriptionValue) descriptionEl.innerHTML = this.descriptionValue
    else descriptionEl.innerHTML = ''

    try {
      const { computePosition, flip, shift, offset, arrow } = window.FloatingUIDOM

      //* Make it visible
      tooltipEl.style.display = 'block'

      //* Compute the position
      computePosition(this.element, tooltipEl, {
        placement: this.positionValue || 'top',
        middleware: [offset(5), flip(), shift({ padding: 2 }), arrow({ element: arrowElement })],
      }).then(({ x, y, placement, middlewareData }) => {
        if (!tooltipEl || !arrowElement) return
        Object.assign(tooltipEl.style, {
          left: `${x}px`,
          top: `${y}px`,
        })

        const { x: arrowX, y: arrowY } = middlewareData.arrow

        const staticSide: string =
          {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right',
          }[placement.split('-')[0]] || ''

        Object.assign(arrowElement.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-4px',
        })
      })
    } catch (e) {
      this.loadfloatingUIScriptCore()
      this.loadfloatingUIScriptDOM()
      return
    }
  }

  hideTooltip = () => {
    if (tooltipEl) tooltipEl.style.display = 'none'
  }

  loadfloatingUIScriptCore() {
    let floatingUIScriptCore: HTMLScriptElement | null = document.getElementById(
      'floating_ui_script_core'
    ) as HTMLScriptElement | null
    if (floatingUIScriptCore) {
      floatingUIScriptCore.remove()
      floatingUIScriptCore = null
    }

    floatingUIScriptCore = document.createElement('script')
    floatingUIScriptCore.src = 'https://cdn.jsdelivr.net/npm/@floating-ui/core@1.5.0'
    floatingUIScriptCore.type = 'text/javascript'
    floatingUIScriptCore.id = 'floating_ui_script_core'
    document.head.appendChild(floatingUIScriptCore)
  }

  loadfloatingUIScriptDOM() {
    let floatingUIScriptDOM: HTMLScriptElement | null = document.getElementById(
      'floating_ui_script_dom'
    ) as HTMLScriptElement | null
    if (floatingUIScriptDOM) {
      floatingUIScriptDOM.remove()
      floatingUIScriptDOM = null
    }

    floatingUIScriptDOM = document.createElement('script')
    floatingUIScriptDOM.src = 'https://cdn.jsdelivr.net/npm/@floating-ui/dom@1'
    floatingUIScriptDOM.type = 'text/javascript'
    floatingUIScriptDOM.id = 'floating_ui_script_dom'
    document.head.appendChild(floatingUIScriptDOM)
  }

  disconnect() {
    // Remove the tooltip element from the DOM
    const tooltipEl = this.element.querySelector('.tooltip')
    if (tooltipEl) {
      tooltipEl.remove()
    }

    // Remove the event listeners from the element
    this.element.removeEventListener('mouseenter', this.showTooltip)
    this.element.removeEventListener('focus', this.showTooltip)
    this.element.removeEventListener('mouseleave', this.hideTooltip)
    this.element.removeEventListener('click', this.hideTooltip)
    this.element.removeEventListener('blur', this.hideTooltip)
  }
}
